/* ============================================================
   Safe HTML templating

   Audit finding A-10: every component built DOM from template strings
   with raw interpolation. Nothing was exploitable while all inputs were
   static, but the roadmap adds company names, notes and NTB free text.
   The first user-supplied string to reach one of those templates would
   be stored XSS — and it would land in the exported PDF too.

   Usage:
     el.innerHTML = html`<h1>${userInput}</h1>`;         // escaped
     el.innerHTML = html`<div>${raw(trustedSvg)}</div>`; // opt-out
     html`<ul>${items.map(i => html`<li>${i}</li>`)}</ul>`

   `html` returns a SafeHtml (a String subclass), not a plain string, so
   that a nested template is recognised as already-escaped rather than
   escaped a second time. Assigning one to `innerHTML` coerces it via
   toString, so call sites need no ceremony.

   Plain strings are always escaped — including plain strings returned
   from a nested function. If you build markup by hand, wrap it in raw()
   and take responsibility for it.
   ============================================================ */

class SafeHtml extends String {}

const ENTITIES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};

/** Escape a value for HTML text or a quoted attribute. */
export function escapeHtml(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/[&<>"']/g, (c) => ENTITIES[c]);
}

/**
 * Mark a string as already-safe markup, exempting it from escaping.
 * Only pass author-controlled strings — never user input.
 */
export function raw(value) {
  return new SafeHtml(value === null || value === undefined ? '' : String(value));
}

export function isSafe(value) {
  return value instanceof SafeHtml;
}

function resolve(value) {
  if (value === null || value === undefined || value === false) return '';
  if (value instanceof SafeHtml) return value.toString();
  if (Array.isArray(value)) return value.map(resolve).join('');
  return escapeHtml(value);
}

/** Tagged template that escapes every interpolation by default. */
export function html(strings, ...values) {
  let out = strings[0];
  for (let i = 0; i < values.length; i++) {
    out += resolve(values[i]) + strings[i + 1];
  }
  return new SafeHtml(out);
}

/** Escape a value for use inside an inline style or custom property. */
export function cssSafe(value) {
  return String(value ?? '').replace(/[^a-zA-Z0-9#(),.%\-\s/]/g, '');
}
